import { useEffect, useState, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { Plane, RefreshCw, Radio, Gauge, Mountain, Navigation, Users, PlaneTakeoff, PlaneLanding, History, MapPin } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { getLiveFlights, getRecentRoutes } from "@/lib/api";
import { initials } from "@/lib/format";

const R = 3440.065; // nmi
const toRad = (d) => (d * Math.PI) / 180;
const haversine = (a, b) => {
  if (!a?.lat || !b?.lat) return 0;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const phaseColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("enroute") || s.includes("cruise")) return "text-emerald-400 border-emerald-400/40 bg-emerald-400/10";
  if (s.includes("taxi") || s.includes("board") || s.includes("push")) return "text-[#D4AF37] border-[#D4AF37]/40 bg-[#D4AF37]/10";
  if (s.includes("climb") || s.includes("desc") || s.includes("approach")) return "text-sky-400 border-sky-400/40 bg-sky-400/10";
  return "text-[#A1A1AA] border-white/15 bg-white/5";
};

const MAP_IMG =
  process.env.REACT_APP_MAP_IMAGE_URL ||
  "https://upload.wikimedia.org/wikipedia/commons/8/83/Equirectangular_projection_SW.jpg";

const progressOf = (f) => {
  const total = haversine(f.dpt, f.arr);
  const done = haversine(f.dpt, f.position);
  if (!total) return 0;
  return Math.max(4, Math.min(100, (done / total) * 100));
};

const FlightMarker = ({ f }) => {
  const { lat, lon, heading } = f.position || {};
  if (typeof lat !== "number" || typeof lon !== "number") return null;
  const x = ((lon + 180) / 360) * 100;
  const y = ((90 - lat) / 180) * 100;
  return (
    <div
      className="group absolute -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${x}%`, top: `${y}%` }}
      data-testid={`map-marker-${f.id}`}
    >
      <Plane
        className="h-4 w-4 text-[#D4AF37] drop-shadow-[0_0_6px_rgba(212,175,55,0.8)] transition-transform group-hover:scale-150"
        style={{ transform: `rotate(${(heading || 0) - 45}deg)` }}
      />
      <span className="pointer-events-none absolute left-1/2 top-5 -translate-x-1/2 whitespace-nowrap rounded bg-[#0A0B10] px-2 py-0.5 font-mono-data text-[10px] text-[#FDFBF7] opacity-0 transition-opacity group-hover:opacity-100">
        {f.callsign}
      </span>
    </div>
  );
};

const projX = (lon) => ((lon + 180) / 360) * 100;
const projY = (lat) => ((90 - lat) / 180) * 100;

// Faded great-line overlay + endpoint dots for recently-flown routes.
const RecentRoutesOverlay = ({ routes }) => {
  const airports = useMemo(() => {
    const map = {};
    routes.forEach((r) => {
      [r.dpt, r.arr].forEach((a) => {
        if (a?.lat != null && a?.lon != null && a.id) map[a.id] = a;
      });
    });
    return Object.values(map);
  }, [routes]);

  return (
    <>
      <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden data-testid="recent-routes-lines">
        {routes.map((r) => (
          <line
            key={r.id}
            x1={projX(r.dpt.lon)} y1={projY(r.dpt.lat)}
            x2={projX(r.arr.lon)} y2={projY(r.arr.lat)}
            stroke="#D4AF37" strokeWidth="1.1" strokeOpacity="0.35"
            strokeDasharray="4 3" strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      {airports.map((a) => (
        <span
          key={a.id}
          className="absolute -translate-x-1/2 -translate-y-1/2"
          style={{ left: `${projX(a.lon)}%`, top: `${projY(a.lat)}%` }}
          title={`${a.id} — ${a.name || ""}`}
        >
          <span className="block h-1.5 w-1.5 rounded-full bg-[#D4AF37] shadow-[0_0_6px_rgba(212,175,55,0.9)]" />
        </span>
      ))}
    </>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const RecentRouteCard = ({ r, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35, delay: index * 0.04 }}
    data-testid={`recent-route-${r.id}`}
    className="rounded-2xl border border-white/5 bg-[#12141D] p-5 transition-colors duration-300 hover:border-[#D4AF37]/25"
  >
    <div className="flex items-center justify-between">
      <p className="font-mono-data text-sm font-bold text-[#FDFBF7]">{r.callsign || "—"}</p>
      <span className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] font-semibold text-[#A1A1AA]">{r.aircraft || "—"}</span>
    </div>
    <div className="mt-4 flex items-center gap-3">
      <span className="font-mono-data text-lg font-bold text-[#FDFBF7]">{r.dpt?.id || "—"}</span>
      <div className="relative h-[2px] flex-1 bg-white/10">
        <div className="absolute inset-0 bg-gradient-to-r from-[#8A1538] to-[#D4AF37] opacity-60" />
        <Plane className="absolute left-1/2 top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 -rotate-45 text-[#D4AF37]" />
      </div>
      <span className="font-mono-data text-lg font-bold text-[#FDFBF7]">{r.arr?.id || "—"}</span>
    </div>
    <p className="mt-3 flex items-center gap-1.5 text-[11px] text-[#A1A1AA]">
      <MapPin className="h-3 w-3 text-[#8A1538]" /> Flown {fmtDate(r.submitted_at)}
    </p>
  </motion.div>
);

const FlightCard = ({ f, index }) => {
  const progress = progressOf(f);
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      data-testid={`live-flight-${f.id}`}
      className="rounded-2xl border border-white/5 bg-[#12141D] p-5 transition-colors duration-300 hover:border-[#D4AF37]/30"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {f.pilot?.avatar ? (
            <img src={f.pilot.avatar} alt="" className="h-10 w-10 rounded-full object-cover" />
          ) : (
            <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/30 bg-[#8A1538]/20 font-mono-data text-xs font-bold text-[#D4AF37]">
              {initials(f.pilot?.name)}
            </span>
          )}
          <div>
            <p className="font-mono-data text-sm font-bold text-[#FDFBF7]">{f.callsign || "—"}</p>
            <p className="text-xs text-[#A1A1AA]">{f.pilot?.name || "Pilot"}</p>
          </div>
        </div>
        <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold ${phaseColor(f.status_text || f.phase)}`}>
          {f.status_text || f.phase || f.status || "Active"}
        </span>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <span className="font-mono-data text-lg font-bold text-[#FDFBF7]">{f.dpt?.id || "—"}</span>
        <div className="relative h-[2px] flex-1 bg-white/10">
          <div className="absolute left-0 top-0 h-full bg-[#8A1538]" style={{ width: `${progress}%` }} />
          <Plane
            className="absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 -translate-x-1/2 -rotate-45 text-[#D4AF37]"
            style={{ left: `${progress}%` }}
          />
        </div>
        <span className="font-mono-data text-lg font-bold text-[#FDFBF7]">{f.arr?.id || "—"}</span>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-3 text-center">
        <div>
          <p className="flex items-center justify-center gap-1 text-[11px] text-[#A1A1AA]"><Plane className="h-3 w-3" /> A/C</p>
          <p className="mt-0.5 font-mono-data text-xs text-[#FDFBF7]">{f.aircraft?.icao || f.aircraft?.registration || "—"}</p>
        </div>
        <div>
          <p className="flex items-center justify-center gap-1 text-[11px] text-[#A1A1AA]"><Mountain className="h-3 w-3" /> ALT</p>
          <p className="mt-0.5 font-mono-data text-xs text-[#FDFBF7]">{f.position?.altitude ? `${Math.round(f.position.altitude).toLocaleString()} ft` : "—"}</p>
        </div>
        <div>
          <p className="flex items-center justify-center gap-1 text-[11px] text-[#A1A1AA]"><Gauge className="h-3 w-3" /> GS</p>
          <p className="mt-0.5 font-mono-data text-xs text-[#FDFBF7]">{f.position?.gs ? `${Math.round(f.position.gs)} kt` : "—"}</p>
        </div>
      </div>
    </motion.div>
  );
};

const HubColumn = ({ title, icon: Icon, rows, max }) => (
  <div className="flex-1" data-testid={`hub-col-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <p className="text-overline mb-3 flex items-center gap-2 text-[#A1A1AA]">
      <Icon className="h-3.5 w-3.5 text-[#D4AF37]" /> {title}
    </p>
    <div className="space-y-2.5">
      {rows.length === 0 && <p className="text-sm text-[#A1A1AA]/60">—</p>}
      {rows.map(([id, count]) => (
        <div key={id} className="flex items-center gap-3" data-testid={`hub-row-${id}`}>
          <span className="w-12 flex-shrink-0 font-mono-data text-sm font-bold text-[#FDFBF7]">{id}</span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-[#8A1538] to-[#D4AF37]"
              style={{ width: `${(count / max) * 100}%` }}
            />
          </div>
          <span className="w-5 flex-shrink-0 text-right font-mono-data text-xs text-[#D4AF37]">{count}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function LiveFlights() {
  const [flights, setFlights] = useState([]);
  const [recentRoutes, setRecentRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (initial = false) => {
    if (!initial) setRefreshing(true);
    try {
      const data = await getLiveFlights();
      setFlights(data.data || []);
    } catch {
      /* keep previous */
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    getRecentRoutes().then((r) => setRecentRoutes(r || [])).catch(() => {});
  }, []);

  const noLive = !loading && flights.length === 0;
  const showRoutes = noLive && recentRoutes.length > 0;

  const hubs = useMemo(() => {
    const dep = {};
    const arr = {};
    flights.forEach((f) => {
      if (f.dpt?.id) dep[f.dpt.id] = (dep[f.dpt.id] || 0) + 1;
      if (f.arr?.id) arr[f.arr.id] = (arr[f.arr.id] || 0) + 1;
    });
    const top = (o) => Object.entries(o).sort((a, b) => b[1] - a[1]).slice(0, 5);
    const depTop = top(dep);
    const arrTop = top(arr);
    const max = Math.max(1, ...depTop.map((r) => r[1]), ...arrTop.map((r) => r[1]));
    return { depTop, arrTop, max };
  }, [flights]);

  return (
    <div className="min-h-screen bg-[#0A0B10]" data-testid="live-flights-page">
      <Navbar />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-28 lg:px-10 lg:pt-32">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="text-overline flex items-center gap-2 text-[#8A1538]">
              <Radio className="h-4 w-4" /> Live Now
            </p>
            <h1 className="mt-3 font-display text-4xl font-black tracking-tight text-[#FDFBF7] lg:text-5xl [text-wrap:balance]">
              Live Flights
            </h1>
            <p className="mt-3 max-w-xl text-base text-[#A1A1AA] [text-wrap:pretty]">
              Track every Air India Virtual aircraft currently airborne across the
              network — no login required.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-[#FDFBF7]" data-testid="live-count">
              <Users className="h-4 w-4 text-[#D4AF37]" />
              <span className="font-mono-data font-bold">{flights.length}</span> airborne
            </span>
            <button
              onClick={() => load(false)}
              data-testid="live-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#8A1538] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#A81A45]"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>

        {/* Live map */}
        <div
          className="relative mt-8 aspect-[2/1] w-full overflow-hidden rounded-2xl border border-white/10 bg-[#0d1526]"
          data-testid="live-map"
        >
          <img src={MAP_IMG} alt="World map" className="absolute inset-0 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B10]/60 to-transparent" />
          {flights.map((f) => (
            <FlightMarker key={f.id} f={f} />
          ))}
          {showRoutes && <RecentRoutesOverlay routes={recentRoutes} />}
          {noLive && (
            <div className="absolute inset-x-0 bottom-4 flex justify-center px-4">
              <p className="flex items-center gap-2 rounded-full border border-white/10 bg-[#0A0B10]/80 px-4 py-2 text-center text-xs text-[#A1A1AA] backdrop-blur" data-testid="live-map-empty">
                <Navigation className="h-3.5 w-3.5 text-[#D4AF37]" />
                {showRoutes
                  ? "No active flights right now — showing recently flown routes."
                  : "No aircraft are airborne right now. Check back soon."}
              </p>
            </div>
          )}
        </div>

        {/* Busiest hubs right now */}
        {flights.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            data-testid="hub-panel"
            className="mt-6 rounded-2xl border border-white/5 bg-[#12141D] p-6"
          >
            <div className="mb-5 flex items-center gap-2">
              <Radio className="h-4 w-4 text-[#8A1538]" />
              <h2 className="font-display text-lg font-bold text-[#FDFBF7]">Busiest Hubs Right Now</h2>
            </div>
            <div className="flex flex-col gap-8 sm:flex-row">
              <HubColumn title="Top Departures" icon={PlaneTakeoff} rows={hubs.depTop} max={hubs.max} />
              <div className="hidden w-px bg-white/10 sm:block" />
              <HubColumn title="Top Arrivals" icon={PlaneLanding} rows={hubs.arrTop} max={hubs.max} />
            </div>
          </motion.div>
        )}

        {/* Flight cards */}
        {loading ? (
          <p className="mt-10 text-center text-sm text-[#A1A1AA]">Loading live traffic…</p>
        ) : flights.length > 0 ? (
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {flights.map((f, i) => (
              <FlightCard key={f.id} f={f} index={i} />
            ))}
          </div>
        ) : showRoutes ? (
          <div className="mt-8" data-testid="recent-routes-panel">
            <div className="mb-5 flex items-center gap-2">
              <History className="h-4 w-4 text-[#8A1538]" />
              <h2 className="font-display text-lg font-bold text-[#FDFBF7]">Recently Flown</h2>
            </div>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {recentRoutes.map((r, i) => (
                <RecentRouteCard key={r.id} r={r} index={i} />
              ))}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}
